<script setup lang="ts">

import acLoader from "@/components/reusable/acLoader.vue"
import AiMessageDisplayer from "./components/aiMessageDisplayer.vue"
import ChatTextarea from "./components/chatTextarea.vue"
import CollaborativeSidebar from "./components/collaborativeSidebar.vue"
import StepAddAiProjectForm from "./components/stepCreateProjectForm.vue"
import TeleportsAcMediumModal from "../reusable/teleports/acMediumModal.vue"
import { createNewProject, assignProjectToUser, useGetAllProjectMessages, postProjectMessage, processAIProject } from "@/composables/aiChatFunctions"
import { modalClosed } from "@/mixins/visualFormatters"
import type { AICollabMessage, AICollabProject } from "@/types"
import { useStore } from 'vuex';
import { computed, watch, ref, onMounted, nextTick } from 'vue';
import { postData } from "@/mixins/modularFirebaseOperations"
const store = useStore()
const isAIWriting = ref(false)
const selectedProject = computed(() => store.getters['ai/selectedProject']);
const userProfile = computed(() => store.getters['user/userProfile']);
const chatMessageContainer = ref(null);
const collaborativeSidebarRef = ref(null);
import ProcessedFilesDisplayer from "./components/processedFilesDisplayer.vue"

const user = {
    id: userProfile.value.uid,
    email: userProfile.value.email,
    name: userProfile.value.name,
    picture: userProfile.value.picture,
    allowsEdit: userProfile.value.allowsEdit
}


const isFileListDisplayed = ref(true)
const isCreatingProject = ref(false)


const toggleFileListDisplay = () => {
    isFileListDisplayed.value = !isFileListDisplayed.value
}
const handleProjectCreated = async (project: AICollabProject) => {
    
    // Assign the project to the user who created it

    project.members.push({
        id: user.id,
        name: user.name,
    })


    try {
        // First, create the project in the database
        const newProjectId = await createNewProject(project)
        if (newProjectId) {


            // Then, assign the project to the users
            for (const member of project.members) {
                await assignProjectToUser(newProjectId, member.id)
            }
            // Refresh the projects list in the sidebar
            await collaborativeSidebarRef.value?.refreshProjects()
            store.dispatch('ai/setSelectedProject', newProjectId)
            
            // MAke a request to the backend to start processing project info

            await processAIProject(newProjectId)
        }
    } catch (error) {
        alert("Error creating project: " + error)
    } finally {
        isCreatingProject.value = false
        modalClosed()
    }
}

// Create refs to hold chat messages and loading state
const chatMessages = ref<AICollabMessage[]>([]);
const isLoadingChatMessages = ref(false);

let cleanup = null;
const aiResponseReceivedRef = ref(false);

// Function to update chat messages when session changes
const updateChatMessages = () => {
    // Clean up previous listener if it exists
    if (cleanup) {
        cleanup();
    }
    
    // Only fetch messages if we have a valid session
    if (selectedProject.value && selectedProject.value.id) {
        const result = useGetAllProjectMessages(
            selectedProject.value.id, 
        );
        
        // Update our refs
        chatMessages.value = result.messages.value;
        isLoadingChatMessages.value = result.isLoadingMessages.value;
        cleanup = result.cleanup;
        
        // Update the aiResponseReceived ref
        aiResponseReceivedRef.value = result.aiResponseReceived.value;
        
        // Set up a direct watcher for messages to detect AI responses
        watch(result.messages, (newMessages) => {
            // Check if the last message is from AI
            if (newMessages.length > 0 && newMessages[newMessages.length - 1].type === 'ai') {
                isAIWriting.value = false;
                scrollToBottom();
            }
        }, { deep: true });
    }
};

// Watch for AI response to update isAIWriting state
watch(() => aiResponseReceivedRef.value, (newValue) => {
    if (newValue) {
        isAIWriting.value = false;
        // Scroll to bottom when AI response is received
        scrollToBottom();
    }
}, { immediate: true });

// Watch for changes in chatMessages to scroll to bottom when new messages are added
watch(chatMessages, (newMessages) => {
    scrollToBottom();
    
    // Check if the last message is from AI and we're still in AI writing state
    if (newMessages.length > 0 && 
        newMessages[newMessages.length - 1].type === 'ai' && 
        isAIWriting.value) {
        isAIWriting.value = false;
    }
}, { deep: true });

// Initialize chat messages after component is mounted
onMounted(() => {
    updateChatMessages();
});

// Watch for changes in selectedSession
watch(selectedProject, () => {
    updateChatMessages();
}, { deep: true });

const messagePosted = async (message: string) => {

    if(message.length === 0) {
        return;
    }

    const userMessage: AICollabMessage = {
            id: 'temp-' + Date.now(), // Temporary ID that will be replaced by Firebase
            content: message,
            type: 'human',
            timestamp: new Date(),
            from: user // Add the from field with the current user
        };

    if(message.startsWith('@')) {
        const cleanMessage = {
            content: message,
            type: 'human',
            timestamp: new Date(),
            from: user
        };
        await postData(cleanMessage, `ai/collective/projects/${selectedProject.value.id}/messages`)
        return;
    }

    try {
        // Reset aiResponseReceived to ensure we catch the next response
        aiResponseReceivedRef.value = false;
        
        // Add user message to UI immediately

        chatMessages.value.push(userMessage);
        
        // Scroll to bottom to show the new message
        scrollToBottom();
        
        // Set AI writing state
        isAIWriting.value = true;
        
        // Set a timeout to reset isAIWriting if no response is received
        const timeoutId = setTimeout(() => {
            isAIWriting.value = false;
        }, 10000); // 10 seconds timeout
        
        // Send message to backend
        await postProjectMessage(message, selectedProject.value.id, user);
        
        // Clear the timeout since we're waiting for the real-time listener to update isAIWriting
        clearTimeout(timeoutId);
        
        // Set a shorter timeout to check if we need to manually reset isAIWriting
        setTimeout(() => {
            // If we're still in AI writing state after 5 seconds, check if we have an AI message
            if (isAIWriting.value && chatMessages.value.length > 0) {
                const lastMessage = chatMessages.value[chatMessages.value.length - 1];
                if (lastMessage.type === 'ai') {
                    isAIWriting.value = false;
                }
            }
        }, 5000);
        
        // AI writing state will be cleared when the real-time listener receives the AI response
    } catch (error) {
        console.error(error);
        isAIWriting.value = false;
    }
}

const handleProjectChanged = (projectId: string) => {
    store.dispatch('ai/setSelectedProject', projectId)
    
}

const scrollToBottom = async () => {
    await nextTick();

    // Use a shorter timeout to make it more responsive
    setTimeout(() => {
        if (chatMessageContainer.value) {
            chatMessageContainer.value.scrollTop = chatMessageContainer.value.scrollHeight;
            
            // Add a second scroll after a short delay to ensure it works even if content is still loading
            setTimeout(() => {
                if (chatMessageContainer.value) {
                    chatMessageContainer.value.scrollTop = chatMessageContainer.value.scrollHeight;
                }
            }, 100);
        }
    }, 50);
};

</script>

<template>
    <div class="component-container relative">
        <div class="main-container no-projects" v-if="!selectedProject">
            <img src="@/assets/images/no-projects-dansuk.png" alt="No projects" />
            <h3>
                Henüz hiçbir projeniz yok.
            </h3>
            <p>
                Eğer yetkiniz varsa, hemen yeni bir proje oluşturabilirsiniz. Eğer proje oluşturmaya yönelik bir buton görmüyorsanız, lütfen ekibinizden sizi bir projeye eklemelerini isteyin.
            </p>
            <button v-if="userProfile.allowsEdit" class="ac-primary-button text-2xl" @click="isCreatingProject = true">
                Proje Oluştur
            </button>
        </div>
        <div v-else class="ai-chat main-container">
        <!-- Sidebar -->
        <collaborative-sidebar @create-new-project="isCreatingProject = true" @projectChanged="handleProjectChanged" ref="collaborativeSidebarRef" />
        <div class="chat-area-container">
            <div class="chat-area">
                <div v-if="selectedProject.isProcessingInfoBase && chatMessages.length === 0" class="notification bg-yellow-500 text-white top-24 left-[7%] absolute p-4 rounded-lg border border-orange-300">
                    <p>
                        Bu proje için kullanılacak veri kaynağı şu anda işleniyor. Seçtiğiniz dosya / klasörün boyutuna göre bu işlem birkaç dakika sürebilir. Lütfen veri kaynağının aktarılmasını bekleyin.
                    </p>
                </div>
                <div ref="chatMessageContainer" id="chatMessages" class="chat-messages-container">
                    <div class="disappear-zone"></div>
                    <div v-if="isLoadingChatMessages">
                        <ac-loader />
                    </div>
        
                    <div v-else-if="chatMessages.length === 0" class="no-messages">
                        Henüz hiç mesaj yok.
                    </div>
                    <ai-message-displayer v-for="(message) in chatMessages" :key="message.id"
                        :message="message" 
                        :collab="true"
                        :mine="message.from?.id === user.id" />
                    <div class="anchor"></div>
                </div>
            </div>
            <chat-textarea id="collabTextArea" @messagePosted="messagePosted" :isAIWriting="isAIWriting" :projectMembers="selectedProject?.members || [] " />
        </div>
        <div :class="{'is-displayed': isFileListDisplayed}" class="file-display-area relative">

                <processed-files-displayer class="processed-files-displayer" v-if="selectedProject.processedFiles && selectedProject.processedFiles.length > 0 && isFileListDisplayed" />
            <button :class="{'not-displayed': !isFileListDisplayed}" type="button" class="close-button" @click="toggleFileListDisplay">
            <span class="">
                >
            </span>
        </button>
        </div>
    </div>
    <transition
        enter-active-class="animate__animated animate__fadeIn"
        leave-active-class="animate__animated animate__fadeOut"
        > 
            <teleports-ac-medium-modal title="Proje Oluştur" width="50vw" height="60vh" v-if="isCreatingProject" @modal-closed="isCreatingProject = false; ">     
                <step-add-ai-project-form @cancelled="isCreatingProject = false; modalClosed()" @submitted="handleProjectCreated"/>
            </teleports-ac-medium-modal>
        </transition>
    </div>

   
</template>

<style scoped>

.no-projects{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    text-align: center;
    color: #696969;
    margin: 2rem auto;
    background-color: rgba(255, 255, 255, 0.5);
    grid-column: 1 / -1;
}

.no-projects img{
    width: 30rem;

}

.no-projects h3{
    font-size: 3.2rem;
    font-weight: 400;
    color: black;
}

.no-projects p{
    font-size: 1.8rem;
    color: black;
    max-width: 50%;
}



.ai-chat {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin-right: auto;
  gap: 2.5%;
  height: calc(100svh - 9rem);
  overflow: hidden;
  color: #696969;
  font-size: 1.6rem;
  line-height: 1.56;
  letter-spacing: 0.5px;
  background-color: #fff;
  position: relative;

}
.chat-area-container {
    padding: 2.4rem 3.2rem 2.4rem 0;
    flex-basis: 80%;
    height: 100%;
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-self: flex-end;
    justify-content: end;
}

.chat-area{
    width: 100%;
    height: auto;
    overflow: auto;
}

.chat-messages-container {
    z-index: 1;
    position: relative;
    height: 100%;
    overflow: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;
    display: flex;
    flex-direction: column;
    overscroll-behavior-y: contain;
    scroll-snap-type: y proximity;
    scroll-behavior: smooth;
    
    margin: 0 auto;

}

.no-messages {
    text-align: center;
    padding: 2rem;
    color: #999;
    font-style: italic;
}

.file-display-area{
    flex-basis: 2%;
    height: calc(100vh - 9rem);
    transition: flex-basis 0.3s ease-in-out, opacity 0.15s ease;
    white-space: nowrap;
    font-size: 10px;

}

.file-display-area.is-displayed {
    position: relative;
    flex-basis: 15%;
    white-space: normal;
}

.file-display-area:not(.is-displayed) {
    opacity: 0.5;
}

.processed-files-displayer {
    border-left: 1px solid #ccc;
    position: relative;
    height: 100%;
    padding: 2.4rem 1.6rem;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
}

.close-button {
    position: absolute;
    bottom: 1.6rem;
    left: -1.6rem;
    background-color: transparent;
    border: none;
    cursor: pointer;
    background-color: white;
    border-radius: 50%;
    padding: 0.8rem;
    border: 1px solid #ccc;
    font-size: 2.4rem;
    height: 4rem;
    width: 4rem;
    color: black;
    width: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-button:hover {
    background-color: #f0f0f0;
}

.close-button.not-displayed span{
  transform: rotateY(132deg);
}

.close-button span {
    margin-bottom: 0;
    font-size: 2.4rem;
    color: hsl(0, 0%, 63%);
}
</style>